import { nth } from 'ramda'
import { TaskOperations } from '../../../types'
import { ChordsTypeKey } from '../../tasks/use-chords'
import {
  ChannelSettings,
  OperationSettings
} from '../use-persisted-channel-settings'
import { OperationBeatChordsResult } from '../../../types/task.types'
import { findOperations } from '../../../lib/operations'

const ALTERED_STEM_MAP = {
  vocals: 'stem_altered_vocal',
  backing_vocals: 'stem_altered_bkgvocal',
  drums: 'stem_altered_drum',
  guitars: 'stem_altered_guitar',
  bass: 'stem_altered_bass',
  piano: 'stem_altered_piano',
  strings: 'stem_altered_strings',
  accompaniment: 'stem_altered_instrumental',
  other: 'stem_altered_other'
} as const

function checkSeparateOperationHasTuning(
  operations: TaskOperations[]
): boolean {
  const [separateOperation, beatschordsOperation] = findOperations(
    operations,
    ['separate', 'beatschords'],
    { status: 'COMPLETED' }
  )
  const beatschordsResult = beatschordsOperation?.result as
    | OperationBeatChordsResult
    | undefined
  const originalTuning = beatschordsResult?.estimatedTuning

  if (!separateOperation || !originalTuning) {
    return false
  }

  const raw = localStorage.getItem(`tuning-${separateOperation.id}`) || ''
  const tuning = parseInt(raw, 10)
  return !Number.isNaN(tuning) && tuning !== originalTuning
}

function hasChannelChanged(channel: ChannelSettings): boolean {
  return (
    (channel.volume !== undefined && channel.volume !== 0.75) ||
    (channel.volume !== undefined && channel.pan !== 0) ||
    !!channel.isMuted ||
    !!channel.isSolo
  )
}

function extractAlteredStems(
  channels: ChannelSettings[]
): (typeof ALTERED_STEM_MAP)[keyof typeof ALTERED_STEM_MAP][] {
  return channels
    .filter(hasChannelChanged)
    .map(
      (channel) => ALTERED_STEM_MAP[channel.id as keyof typeof ALTERED_STEM_MAP]
    )
    .filter(Boolean)
}

export function getMixerInitialState(
  operations: TaskOperations[] | undefined,
  settings: OperationSettings | undefined
): string[] {
  const flagsMap = {
    ai_lyrics_on: settings?.panels?.showLyrics,
    chords_on: settings?.panels?.showChords,
    count_in_on: settings?.general?.countIn?.enabled,
    speed_changed: settings?.general && settings?.general?.playbackRate !== 100,
    pitch_changed:
      settings?.general && settings?.general?.pitchShiftCents !== 0,
    sections_on: settings?.panels?.showSections,
    tuning_changed: checkSeparateOperationHasTuning(operations || []),
    trim_changed: settings?.general?.loop?.isLooping,
    chords_easy: settings?.general?.typeChords === ChordsTypeKey.easy,
    chords_medium: settings?.general?.typeChords === ChordsTypeKey.medium,
    chords_advanced: settings?.general?.typeChords === ChordsTypeKey.advanced,
    master_volume_changed:
      settings?.general && settings?.general?.masterVolume !== 1
  }
  const flags = Object.entries(flagsMap).filter(nth(1)).map(nth(0))

  const alteredStems = extractAlteredStems(settings?.channels || [])

  return flags.concat(alteredStems)
}
